/**
 * Theme applier.
 *
 * Tailwind's config reads primary shades from CSS variables like
 * `rgb(var(--color-primary-600) / <alpha-value>)`. This module converts a base
 * hex to 10 shades (50–900) using HSL lightness targets tuned to match
 * Tailwind's own scale, then writes each as an `R G B` triplet on :root so
 * both bg-primary-600 AND bg-primary-600/50 (alpha) keep working.
 */

import { PALETTES, DEFAULT_PALETTE_ID, getPalette } from './palettes';

// ---------- hex <-> HSL helpers ----------
function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return [r, g, b];
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToRgb(h, s, l) {
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (0 <= h && h < 60) { r = c; g = x; }
  else if (60 <= h && h < 120) { r = x; g = c; }
  else if (120 <= h && h < 180) { g = c; b = x; }
  else if (180 <= h && h < 240) { g = x; b = c; }
  else if (240 <= h && h < 300) { r = x; b = c; }
  else if (300 <= h && h < 360) { r = c; b = x; }
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}

// Tailwind-ish target lightness values per shade — treats base color as ~shade 600.
const SHADE_LIGHTNESS = {
  50:  97,
  100: 93,
  200: 85,
  300: 74,
  400: 60,
  500: 50,
  600: 42,   // typical "primary" bucket
  700: 34,
  800: 26,
  900: 18,
};

// Tone down saturation for near-white shades so they don't look neon.
function saturationFor(shade, baseSaturation) {
  if (shade <= 100) return baseSaturation * 0.35;
  if (shade <= 200) return baseSaturation * 0.55;
  if (shade <= 300) return baseSaturation * 0.75;
  return baseSaturation;
}

/**
 * Generate 10 Tailwind-compatible shades from a base color.
 * Returns `{ 50: '2 132 199', 100: '...', ..., 900: '...' }` — space-separated
 * RGB triplets, which is the format `rgb(var(--color-primary-600) / <alpha>)`
 * expects.
 */
export function generateShades(baseHex) {
  const [r, g, b] = hexToRgb(baseHex);
  const [h, s] = rgbToHsl(r, g, b);
  const out = {};
  for (const [shade, targetL] of Object.entries(SHADE_LIGHTNESS)) {
    const [nr, ng, nb] = hslToRgb(h, saturationFor(Number(shade), s), targetL);
    out[shade] = `${nr} ${ng} ${nb}`;
  }
  // Override 600 with the exact base color so button color matches design
  out[600] = `${r} ${g} ${b}`;
  return out;
}

// Adjust an HSL colour's lightness by a delta, clamped to 0..100.
// Positive = lighter, negative = darker.
function lighten(hexOrTriplet, delta) {
  const [r, g, b] = typeof hexOrTriplet === 'string' ? hexToRgb(hexOrTriplet) : hexOrTriplet;
  const [h, s, l] = rgbToHsl(r, g, b);
  const nl = Math.max(0, Math.min(100, l + delta));
  const [nr, ng, nb] = hslToRgb(h, s, nl);
  return `${nr} ${ng} ${nb}`;
}

function tripletFrom(hex) {
  const [r, g, b] = hexToRgb(hex);
  return `${r} ${g} ${b}`;
}

/**
 * Apply a palette to the document by setting CSS variables on :root.
 * Safe to call multiple times.
 *
 * Sets:
 *   --color-primary-{50..900}  → button / link colours (10 shades)
 *   --color-paper              → page background (bg-gray-50 maps to this)
 *   --color-surface            → card background (bg-white maps to this)
 *   --color-surface-2          → subtle contrast surface (bg-gray-100)
 *   --color-border             → dividers (border-gray-200)
 *   --color-accent             → secondary accent (for pink/emerald/etc buttons)
 */
export function applyPalette(paletteId) {
  const p = getPalette(paletteId);
  const root = document.documentElement;

  // Primary shades — 10 tones from base hex
  const shades = generateShades(p.primary);
  for (const [shade, rgb] of Object.entries(shades)) {
    root.style.setProperty(`--color-primary-${shade}`, rgb);
  }

  // Backgrounds derived from palette.paper (page bg)
  root.style.setProperty('--color-paper', tripletFrom(p.paper));
  // Surface (card bg) — brighter than paper for a subtle raised feel.
  // For dark palettes, surface is slightly lighter than paper.
  const [, , paperL] = rgbToHsl(...hexToRgb(p.paper));
  const isDarkPalette = paperL < 50;
  root.style.setProperty('--color-surface', lighten(p.paper, isDarkPalette ? +5 : +3));
  root.style.setProperty('--color-surface-2', lighten(p.paper, isDarkPalette ? +3 : -3));
  root.style.setProperty('--color-border', lighten(p.paper, isDarkPalette ? +12 : -8));

  // Accent (used for secondary accents like pink Visual, emerald Review, etc.)
  root.style.setProperty('--color-accent', tripletFrom(p.accent));

  // Store id so components can react (Theme picker uses this to highlight active)
  root.dataset.palette = paletteId;
  root.dataset.paletteDark = isDarkPalette ? '1' : '0';
}

const CACHE_KEY = 'memcoach-active-palette';

/**
 * Apply the last-known palette instantly from localStorage (avoids first-paint
 * flash of the fallback color), then kick off a background fetch to pick up
 * any admin change. Safe to call once at app boot.
 *
 * On any error, falls back to the default — which is also the fallback baked
 * into tailwind.config.js, so nothing breaks even if fetch and cache both fail.
 */
export function initTheme() {
  // 1. Instant: apply cached palette so first paint is correct.
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached && PALETTES[cached]) applyPalette(cached);
    else applyPalette(DEFAULT_PALETTE_ID);
  } catch (_) {
    applyPalette(DEFAULT_PALETTE_ID);
  }

  // 2. Background: fetch fresh from server, update cache + re-apply if changed.
  fetch('/api/theme', { headers: { 'Accept': 'application/json' } })
    .then(r => r.ok ? r.json() : null)
    .then(data => {
      if (!data?.paletteId || !PALETTES[data.paletteId]) return;
      try { localStorage.setItem(CACHE_KEY, data.paletteId); } catch (_) {}
      if (document.documentElement.dataset.palette !== data.paletteId) {
        applyPalette(data.paletteId);
      }
    })
    .catch(() => { /* keep whatever we applied instantly */ });
}

/**
 * Called by the admin Theme picker after a successful PUT.
 * Updates cache + applies immediately so the admin sees the change without a reload.
 */
export function setActivePalette(paletteId) {
  if (!PALETTES[paletteId]) return;
  try { localStorage.setItem(CACHE_KEY, paletteId); } catch (_) {}
  applyPalette(paletteId);
}
